import { groupLifeProposalsService } from '@/services/proposals/group-life';
import type { GroupLifeConfirmProposalResponse } from '@/services/proposals/group-life';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * "Confirmar" do Resumo da proposta PME.
 *
 * É o PORTÃO DE SUBSCRIÇÃO da proposta: o back-end avalia as alterações
 * pendentes — vidas ou dados da própria proposta — e, se houver, manda para a
 * análise (18) devolvendo `sentToUnderwritingAnalysis`; se não houver, leva
 * para "Em assinatura" (12). SEM alteração nada é reavaliado: a subscrição da
 * cotação já olhou esta massa.
 *
 * Não dispara DocuSign (quem faz isso é o "Assinatura Digital" da etapa de
 * Assinatura) nem recalcula. A partir daqui a proposta não volta a ser
 * editável, e é por isso que o status muda antes da assinatura começar.
 */
export function useConfirmPmeProposal(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation<GroupLifeConfirmProposalResponse, Error, void>({
    mutationFn: async () => {
      const response = await groupLifeProposalsService.confirmProposal(trimmed);

      if (!response.success || !response.data) {
        throw Object.assign(
          new Error(response.error || 'Erro ao confirmar a proposta'),
          { statusCode: response.status }
        );
      }

      return response.data;
    },
    // O status mudou: sem recarregar o snapshot, o wizard continuaria tratando
    // a proposta como editável. Os critérios também: indo para a análise, a
    // grade da mesa precisa trazer os motivos do desvio.
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['group-life-proposal', trimmed],
      });
      await queryClient.invalidateQueries({
        queryKey: ['group-life', 'underwriting-messages', trimmed],
      });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}
